import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import MonthGrid from "@/components/calendar/MonthGrid";
import WeekGrid from "@/components/calendar/WeekGrid";
import EventList from "@/components/calendar/EventList";
import {
  businessToday, groupByDate, rangeFor, shiftAnchor, titleFor, toDate, ymd, THEME,
} from "@/lib/calendar";

const VIEWS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "list", label: "Upcoming" },
];

export const CalendarView = ({ variant = "light", defaultView = "week", onOpenRide, refreshKey = 0, audience = "dispatch", testId = "calendar" }) => {
  const t = THEME[variant];
  const today = businessToday();
  const [view, setView] = useState(defaultView);
  const [anchor, setAnchor] = useState(toDate(today));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = view === "list" && ymd(anchor) === today
        ? await apiClient.get("/calendar/agenda", { params: { days: 30 } })
        : await apiClient.get("/calendar/events", { params: { start: ymd(range.start), end: ymd(range.end) } });
      setEvents(data.events);
    } catch (e) {
      console.error("Calendar load failed", e);
    } finally {
      setLoading(false);
    }
  }, [view, anchor, range, today]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load, refreshKey]);

  const byDate = useMemo(() => groupByDate(events), [events]);
  const pickDay = (d) => { setAnchor(d); setView("day"); };

  return (
    <div className={`rounded-2xl border p-4 md:p-5 ${t.card}`} data-testid={testId}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className={`h-9 w-9 bg-transparent ${t.btn}`} onClick={() => setAnchor(shiftAnchor(view, anchor, -1))} data-testid={`${testId}-prev`}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className={`bg-transparent ${t.btn}`} onClick={() => setAnchor(toDate(today))} data-testid={`${testId}-today`}>Today</Button>
          <Button size="icon" variant="outline" className={`h-9 w-9 bg-transparent ${t.btn}`} onClick={() => setAnchor(shiftAnchor(view, anchor, 1))} data-testid={`${testId}-next`}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h3 className="font-heading font-bold text-base md:text-lg ml-1" data-testid={`${testId}-title`}>{titleFor(view, anchor)}</h3>
          <RefreshCw className={`w-4 h-4 ml-1 ${t.muted} ${loading ? "animate-spin" : "opacity-0"}`} />
        </div>
        <div className={`inline-flex rounded-xl border p-1 ${t.sub}`}>
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors duration-150 ${view === v.key ? t.active : t.muted}`}
              data-testid={`${testId}-view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <p className={`text-xs mb-3 ${t.muted}`}>All times Eastern (America/New_York, daylight saving handled automatically)</p>

      {view === "month" && <MonthGrid anchor={anchor} eventsByDate={byDate} today={today} variant={variant} onPickDay={pickDay} audience={audience} />}
      {view === "week" && <WeekGrid anchor={anchor} eventsByDate={byDate} today={today} variant={variant} onOpen={onOpenRide} audience={audience} />}
      {view === "day" && <EventList days={[anchor]} eventsByDate={byDate} today={today} variant={variant} onOpen={onOpenRide} audience={audience} />}
      {view === "list" && <EventList eventsByDate={byDate} today={today} variant={variant} onOpen={onOpenRide} audience={audience} hideEmptyDays />}
    </div>
  );
};

export default CalendarView;
